import { Fragment, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import BugReportIcon from '@mui/icons-material/BugReport';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { RuleLogsDrawer } from '../components/unwinding/RuleLogsDrawer';
import {
  useNotifications,
  useNotificationTypes,
} from '../api/useNotifications';
import { useNotificationsSocket } from '../hooks/useNotificationsSocket';
import { useNotificationSound } from '../hooks/useNotificationSound';
import { getNotificationDisplay } from '../components/notifications/notification-display';
import {
  NotificationEvent,
  NotificationEventType,
  NotificationsFilter,
} from '../types/notifications.types';

const PRESET_RANGES: Array<{ label: string; minutes: number | null }> = [
  { label: 'All time', minutes: null },
  { label: 'Last 15m', minutes: 15 },
  { label: 'Last 1h', minutes: 60 },
  { label: 'Last 6h', minutes: 360 },
  { label: 'Last 24h', minutes: 60 * 24 },
];

/**
 * Severity chip uses MUI palette names; per-event-type accent comes
 * from `notification-display.ts`. Keeping severity as a separate
 * dimension lets us color the row's left border by event type while
 * still showing severity (info/warning/error) as a small chip — so
 * the operator sees "kind of event" (color) and "urgency" (chip
 * tone) at the same time without conflating them.
 */
const SEVERITY_CHIP_COLOR: Record<
  NotificationEvent['severity'],
  'default' | 'info' | 'warning' | 'error'
> = {
  info: 'info',
  warning: 'warning',
  error: 'error',
};

function severityChipColor(s: NotificationEvent['severity']) {
  return SEVERITY_CHIP_COLOR[s] || 'default';
}

/**
 * Brand-ish colors per platform so the operator can tell at a glance
 * whether a row is about a Kalshi or Polymarket order without
 * reading the platform word. Picked one approach (colored letters,
 * not a logo) so summary lines stay text-only and copyable.
 *
 * Kalshi → emerald (their brand teal-green).
 * Polymarket → bright blue (their brand purple-blue).
 */
const PLATFORM_COLORS: Record<string, string> = {
  kalshi: '#00b894',
  polymarket: '#4a9eff',
};

/**
 * Render a price (in dollars, e.g. 0.0551) as a Kalshi-style cents
 * string ("5.51¢"). Strips trailing zeros after the decimal so 0.5
 * becomes "50¢" and 0.055 becomes "5.5¢" — matches the format
 * Kalshi uses in their own email/in-app notifications.
 */
export function centsFmt(price: number | null | undefined): string {
  if (price == null || !Number.isFinite(price)) return '?';
  const cents = price * 100;
  // toFixed(2) gives a stable decimal then strip trailing 0s and a
  // dangling decimal point. 5.50 → "5.5", 50.00 → "50", 5.51 → "5.51".
  const s = cents.toFixed(2).replace(/\.?0+$/, '');
  return `${s}¢`;
}

/** "yes" / "YES" / "Yes" → "Yes". Falls back to "?" when missing. */
function sideTitle(side: unknown): string {
  if (typeof side !== 'string' || side.length === 0) return '?';
  const lower = side.toLowerCase();
  if (lower === 'yes') return 'Yes';
  if (lower === 'no') return 'No';
  return side.charAt(0).toUpperCase() + side.slice(1).toLowerCase();
}

/**
 * "Sold" / "Bought" depending on action. Unwinding maker fills are
 * always SELL by construction (the engine sells to close positions),
 * so when `action` is missing we default to "Sold". Manual orders
 * carry an explicit action in their payload.
 */
function actionVerb(action: unknown): 'Sold' | 'Bought' {
  if (typeof action !== 'string') return 'Sold';
  return action.toLowerCase() === 'buy' ? 'Bought' : 'Sold';
}

/**
 * Kalshi-email-style fill phrase: "Sold 31.93 shares of Yes at 5.5¢".
 * Used for both Kalshi and Polymarket fill events so the notifications
 * page reads the same way regardless of platform.
 */
function fillPhrase(
  qty: number | string | undefined,
  side: unknown,
  price: number | null | undefined,
  action?: unknown,
): string {
  return `${actionVerb(action)} ${qty ?? '?'} shares of ${sideTitle(side)} at ${centsFmt(price ?? null)}`;
}

/**
 * Partial-fill variant: "Sold 31.93 of 100 shares of Yes at 5.5¢".
 */
function partialFillPhrase(
  filledQty: number | string | undefined,
  totalQty: number | string | undefined,
  side: unknown,
  price: number | null | undefined,
  action?: unknown,
): string {
  return `${actionVerb(action)} ${filledQty ?? '?'} of ${totalQty ?? '?'} shares of ${sideTitle(side)} at ${centsFmt(price ?? null)}`;
}

/**
 * Build the market URLs for an event. Mirrors the Slack message
 * builder's `marketLinks()` helper (services/position-unwinder/.../
 * slack-message.builder.ts) so both surfaces deep-link to the same
 * pages. Returns up to two entries — Kalshi + Polymarket — whichever
 * the event's identifiers permit.
 *
 * The frontend constructs the URLs from `kalshiTicker` /
 * `polyConditionId` directly (same pattern as `OrdersPanel.tsx`'s
 * fallback) rather than relying on a backend-built `marketUrl`
 * field, because notification payloads don't carry one and adding
 * it would duplicate state.
 */
export function notificationMarketLinks(evt: {
  kalshiTicker?: string | null;
  polyConditionId?: string | null;
}): Array<{ platform: 'kalshi' | 'polymarket'; url: string }> {
  const out: Array<{ platform: 'kalshi' | 'polymarket'; url: string }> = [];
  if (evt.kalshiTicker) {
    out.push({
      platform: 'kalshi',
      url: `https://kalshi.com/markets/${evt.kalshiTicker}`,
    });
  }
  if (evt.polyConditionId) {
    out.push({
      platform: 'polymarket',
      url: `https://polymarket.com/event/${evt.polyConditionId}`,
    });
  }
  return out;
}

/**
 * Wrap any standalone `kalshi` / `polymarket` token in the summary
 * string with a colored, bold span so the platform pops visually.
 * Case-insensitive in the match, but the rendered text is always
 * canonicalised to a short form: `Kalshi` and `Poly` — the latter
 * because "Polymarket" is the longest token in any row and crowded
 * out the actual fill phrase. The colored span keeps each platform
 * scannable even with the abbreviation.
 */
const PLATFORM_DISPLAY: Record<string, string> = {
  kalshi: 'Kalshi',
  polymarket: 'Poly',
};

export function colorizePlatforms(text: string): ReactNode {
  if (!text) return text;
  const parts = text.split(/\b(kalshi|polymarket)\b/gi);
  return parts.map((part, i) => {
    const key = part.toLowerCase();
    const color = PLATFORM_COLORS[key];
    if (!color) return <Fragment key={i}>{part}</Fragment>;
    return (
      <Box
        key={i}
        component="span"
        sx={{ color, fontWeight: 600 }}
      >
        {PLATFORM_DISPLAY[key] ?? part}
      </Box>
    );
  });
}

/**
 * Convert `pairName` / `kalshiTicker` etc. into "Pair name" / "Kalshi
 * ticker" so the detail view reads like prose, not source code.
 * Acronyms stay intact (TNA, USD).
 */
function formatKey(key: string): string {
  // Insert spaces before capital letters, then capitalize first char.
  const spaced = key
    .replace(/([A-Z]+)/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/**
 * Stringify a primitive value with light formatting:
 *   - numbers stay as-is (no thousands separators yet — payloads
 *     mix prices/qtys/percentages and we'd guess wrong)
 *   - booleans render as `true` / `false`
 *   - dates / ISO timestamps render via `Date#toLocaleString`
 */
function formatValue(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'string') {
    // Heuristic: ISO 8601 timestamps render in the operator's locale.
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) {
      const d = new Date(v);
      if (!isNaN(d.getTime())) return d.toLocaleString();
    }
    return v;
  }
  if (typeof v === 'number') return String(v);
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
}

/**
 * Walk the event into a flat list of `[label, value]` rows for the
 * expanded detail view. Top-level fields come first, then payload
 * fields (recursed up to 2 levels deep — anything deeper falls
 * back to a JSON-ish summary so the row stays single-line).
 *
 * Skips null/undefined and the redundant `payload` wrapper key —
 * its contents are merged inline with parent breadcrumb prefixes
 * so the operator doesn't need to mentally unwrap nested objects.
 */
function eventDetailRows(evt: NotificationEvent): Array<[string, string]> {
  const rows: Array<[string, string]> = [];
  rows.push(['Id', evt.id]);
  rows.push(['Type', evt.type]);
  rows.push(['Severity', evt.severity]);
  rows.push(['Source', evt.source]);
  rows.push(['Occurred at', formatValue(evt.occurredAt)]);
  if (evt.ruleId) rows.push(['Rule id', evt.ruleId]);
  if (evt.pairId) rows.push(['Pair id', evt.pairId]);
  if (evt.pairName) rows.push(['Pair name', evt.pairName]);
  if (evt.platform) rows.push(['Platform', String(evt.platform)]);
  if (evt.kalshiTicker) rows.push(['Kalshi ticker', evt.kalshiTicker]);
  if (evt.polyConditionId)
    rows.push(['Polymarket condition id', evt.polyConditionId]);

  const payload = evt.payload as Record<string, unknown> | undefined;
  if (payload && typeof payload === 'object') {
    rows.push(...flattenPayload(payload, ''));
  }
  return rows;
}

function flattenPayload(
  obj: Record<string, unknown>,
  prefix: string,
  depth = 0,
): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  for (const [k, v] of Object.entries(obj)) {
    if (v == null) continue;
    const label = prefix ? `${prefix} → ${formatKey(k)}` : formatKey(k);
    if (Array.isArray(v)) {
      // Arrays of objects (e.g. the digest's `top` list of pairs)
      // get expanded entry-by-entry so the operator sees each
      // pair's fields inline. Without this the digest detail view
      // showed only "Top: 5 items" — the per-pair contract counts
      // were buried in `payload.top` and never surfaced. Arrays of
      // primitives keep the compact "N items" summary since
      // expanding them rarely adds signal.
      const isObjectArray =
        v.length > 0 &&
        typeof v[0] === 'object' &&
        v[0] !== null &&
        !Array.isArray(v[0]);
      if (isObjectArray && depth < 2) {
        v.forEach((item, idx) => {
          out.push(
            ...flattenPayload(
              item as Record<string, unknown>,
              `${label} [${idx + 1}]`,
              depth + 1,
            ),
          );
        });
      } else {
        out.push([label, `${v.length} item${v.length === 1 ? '' : 's'}`]);
      }
    } else if (typeof v === 'object' && depth < 2) {
      out.push(
        ...flattenPayload(v as Record<string, unknown>, label, depth + 1),
      );
    } else if (typeof v === 'object') {
      // Past the recursion budget — surface the keys so the
      // operator at least knows the shape, without dumping the
      // tree.
      const keys = Object.keys(v as object).join(', ');
      out.push([label, keys ? `{ ${keys} }` : '{}']);
    } else {
      out.push([label, formatValue(v)]);
    }
  }
  return out;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function readFilterFromParams(params: URLSearchParams): NotificationsFilter {
  const types = params.getAll('type') as NotificationEventType[];
  return {
    from: params.get('from') ?? undefined,
    to: params.get('to') ?? undefined,
    ruleId: params.get('ruleId') ?? undefined,
    pairId: params.get('pairId') ?? undefined,
    types: types.length > 0 ? types : undefined,
    platform: (params.get('platform') as NotificationsFilter['platform']) ?? 'all',
    source: (params.get('source') as NotificationsFilter['source']) ?? 'all',
    severity:
      (params.get('severity') as NotificationsFilter['severity']) ?? 'all',
  };
}

function writeFilterToParams(filter: NotificationsFilter): URLSearchParams {
  const p = new URLSearchParams();
  if (filter.from) p.set('from', filter.from);
  if (filter.to) p.set('to', filter.to);
  if (filter.ruleId) p.set('ruleId', filter.ruleId);
  if (filter.pairId) p.set('pairId', filter.pairId);
  if (filter.types) for (const t of filter.types) p.append('type', t);
  if (filter.platform && filter.platform !== 'all')
    p.set('platform', filter.platform);
  if (filter.source && filter.source !== 'all') p.set('source', filter.source);
  if (filter.severity && filter.severity !== 'all')
    p.set('severity', filter.severity);
  return p;
}

/**
 * Pulls the most operator-relevant fields out of the type-specific
 * payload to render a one-line plain-text summary on each row. The
 * row's expanded panel renders the full event as a key/value list,
 * so this string only needs the "what just happened" gist.
 *
 * Every `EventType` has an explicit case — the default branch is a
 * defensive fallback for an unknown type rolled out by the backend
 * before this map was updated. It still avoids the raw-JSON dump
 * by walking the payload's own keys.
 */
function summarizeEvent(evt: NotificationEvent): string {
  const p = (evt.payload || {}) as any;
  const platform = (p.platform ?? evt.platform ?? '').toString();
  const pair = p.pairName ?? evt.pairName ?? '';
  const sep = ' · ';
  const dollars = (v: unknown): string =>
    v == null || v === '' ? '?' : `$${v}`;

  switch (evt.type) {
    // ─── Order lifecycle ────────────────────────────────────────────
    case 'maker_placed':
      return [platform, pair, `${p.side?.toUpperCase?.() ?? ''} ${p.qty ?? '?'}@${dollars(p.price)}`]
        .filter(Boolean)
        .join(sep);
    case 'maker_adjusted': {
      // Show whichever field(s) actually changed plus the resulting
      // position so the operator can see "what just moved" AND
      // "where the order is now" in one glance. Without the
      // resulting-state fallback, an amend that only changed qty
      // rendered as "$0.58 → $0.58" — accurate but useless. The
      // direction arrow ` → ` is reserved for the field that moved.
      const priceChanged = p.oldPrice !== p.newPrice;
      const qtyChanged = p.oldQty != null && p.oldQty !== p.newQty;
      const priceText = priceChanged
        ? `${dollars(p.oldPrice)} → ${dollars(p.newPrice)}`
        : dollars(p.newPrice);
      const qtyText =
        p.newQty == null
          ? ''
          : qtyChanged
            ? `qty ${p.oldQty} → ${p.newQty}`
            : `qty ${p.newQty}`;
      return [platform, pair, priceText, qtyText]
        .filter(Boolean)
        .join(sep);
    }
    case 'maker_cancelled':
      return [platform, pair, p.reason ?? '']
        .filter(Boolean)
        .join(sep);
    case 'maker_partial_fill':
      // Kalshi-email-style fill phrase. `side` is missing on legacy
      // payloads (older builds didn't thread it through); fall back
      // to "?" so the rest of the line still reads naturally.
      return [
        platform,
        pair,
        partialFillPhrase(p.filledQty, p.totalQty, p.side, p.filledPrice),
      ]
        .filter(Boolean)
        .join(sep);
    case 'partial_fill_accumulated':
      // The delta is the chunk that just filled; the running total
      // is shown in the parenthetical so the operator sees both
      // "what just landed" and "how close are we to the counter min".
      return [
        platform,
        pair,
        `${fillPhrase(p.deltaQty, p.side, p.filledPrice ?? p.fillPrice)} ` +
          `(accumulated ${p.accumulatedQty ?? '?'}/${p.totalQty ?? '?'})`,
      ]
        .filter(Boolean)
        .join(sep);
    case 'maker_filled':
      return [
        platform,
        pair,
        fillPhrase(p.filledQty, p.side, p.filledPrice),
        p.tna != null
          ? `TNA ${Number(p.tna).toFixed?.(2) ?? p.tna}%`
          : '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'counter_placed':
      return [
        p.counterPlatform ?? '',
        pair,
        `${p.orderType?.toUpperCase?.() ?? ''} ${p.qty ?? '?'}@${dollars(p.price)}`,
      ]
        .filter(Boolean)
        .join(sep);
    case 'crossing_executed':
      return [
        platform,
        pair,
        fillPhrase(p.qty ?? p.filledQty, p.side, p.price ?? p.filledPrice),
      ]
        .filter(Boolean)
        .join(sep);
    case 'crossing_partial':
      return [
        platform,
        pair,
        partialFillPhrase(p.filledQty, p.totalQty, p.side, p.filledPrice),
      ]
        .filter(Boolean)
        .join(sep);
    case 'crossing_accumulated':
      return [
        platform,
        pair,
        `${fillPhrase(p.filledQty, p.side, p.filledPrice)} (accumulated, below counter min)`,
      ]
        .filter(Boolean)
        .join(sep);
    // ─── Manual orders ───────────────────────────────────────────────
    case 'manual_order_placed':
      // Placement is pre-fill — show "Placing X to sell..." as a
      // present-progressive phrase. Action+side come from payload.
      return [
        platform,
        p.marketTitle ?? '',
        `${actionVerb(p.action) === 'Bought' ? 'Buying' : 'Selling'} ${p.qty ?? '?'} shares of ${sideTitle(p.side)} at ${centsFmt(p.price ?? null)}`,
      ]
        .filter(Boolean)
        .join(sep);
    case 'manual_order_executed':
      return [
        platform,
        p.marketTitle ?? '',
        fillPhrase(p.filledQty, p.side, p.price, p.action),
      ]
        .filter(Boolean)
        .join(sep);
    // ─── UI + Slack ──────────────────────────────────────────────────
    case 'execution_complete':
      return [
        pair,
        p.effectiveTna != null
          ? `TNA ${Number(p.effectiveTna).toFixed(2)}%`
          : '',
        p.capitalRecovered != null
          ? `capital $${p.capitalRecovered}`
          : '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'error':
      return [
        pair || evt.ruleId || '',
        p.state ?? '',
        p.errorMessage ?? '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'counter_failed':
      return [
        pair,
        p.counterPlatform ?? '',
        p.reason ?? '',
        p.errorMessage ?? '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'out_of_balance':
      return [
        platform,
        pair,
        p.reason ?? '',
        p.balance != null ? `balance ${p.balance}` : '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'rule_below_minimum':
      return [
        platform,
        pair,
        `${p.computedQty ?? '?'}@${dollars(p.computedPrice)} (min ${p.minQty ?? '?'})`,
      ]
        .filter(Boolean)
        .join(sep);
    case 'dust_remaining':
      return [platform, pair, `dust ${p.dustQty ?? '?'}`]
        .filter(Boolean)
        .join(sep);
    case 'market_takeover':
      return [
        platform,
        pair,
        `${p.cancelledCount ?? 0} order(s) cancelled`,
      ]
        .filter(Boolean)
        .join(sep);
    case 'tna_negative':
      return [
        pair,
        p.takerTna != null
          ? `taker TNA ${Number(p.takerTna).toFixed(2)}%`
          : 'taker TNA negative',
      ]
        .filter(Boolean)
        .join(sep);
    case 'net_contracts_digest': {
      // Surface the first few pair names + their net imbalance inline
      // so the operator can scan the digest without expanding it. The
      // row truncates with ellipsis if the list runs long.
      const top = Array.isArray(p.top) ? p.top : [];
      const head = top
        .slice(0, 3)
        .map((t: any) => {
          const n = Number(t?.netDifference);
          const sign =
            Number.isFinite(n) && n > 0 ? '+' : '';
          return `${t?.pairName ?? '?'} ${sign}${t?.netDifference ?? '?'}`;
        })
        .join(', ');
      const rest = top.length > 3 ? `, +${top.length - 3} more` : '';
      const tail = head ? `${head}${rest}` : `${top.length} pair(s)`;
      return `digest threshold $${p.threshold ?? '?'} · ${tail}`;
    }
    case 'emergency_stop': {
      const head = `${p.rulesAffected ?? '?'} rule(s) cancelled`;
      return p.detail ? `${head}${sep}${p.detail}` : head;
    }
    case 'emergency_resume':
      return `${p.rulesResumed ?? '?'} rule(s) resumed`;
    case 'recovery_filled':
      return [
        p.makerPlatform ?? platform,
        pair,
        fillPhrase(p.filledQty, p.side, p.filledPrice),
        `counter ${p.counterAction ?? ''}`,
        p.errorMessage ?? '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'recovery_restored':
      return [
        platform,
        pair,
        `restored ${p.qty ?? '?'} shares of ${sideTitle(p.side)} at ${centsFmt(p.price ?? null)}`,
        p.reason ?? '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'counter_skipped':
      return [
        pair,
        p.reason ?? '',
        p.rawBid != null ? `rawBid ${p.rawBid}` : '',
        p.netBid != null ? `netBid ${p.netBid}` : '',
      ]
        .filter(Boolean)
        .join(sep);
    case 'platform_disconnect':
      return [
        p.platform ?? platform,
        p.wsType ?? '',
        `${p.cancelledCount ?? 0} cancelled`,
      ]
        .filter(Boolean)
        .join(sep);
    case 'platform_reconnect':
      return `${p.platform ?? platform} reconnected`;
    default: {
      // Defensive: an event type we don't have an explicit case for
      // (newer backend than frontend). Walk the top-level payload
      // keys and render `key=value` pairs so the operator still
      // gets a readable line — never a JSON dump.
      const pairs: string[] = [];
      for (const [k, v] of Object.entries(p)) {
        if (v == null) continue;
        if (typeof v === 'object') continue;
        pairs.push(`${k}=${v}`);
        if (pairs.join(' ').length > 120) break;
      }
      return pairs.join(' ') || '(no payload)';
    }
  }
}

function NotificationRow({
  evt,
  onOpenDebug,
}: {
  evt: NotificationEvent;
  onOpenDebug: (ruleId: string, pairName: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const display = getNotificationDisplay(evt.type);
  return (
    <Paper
      variant="outlined"
      sx={{
        // Tight vertical rhythm so more rows fit on screen — the
        // feed is meant to be scanned, not read line by line.
        // Horizontal padding is asymmetric: extra room on the
        // right so the timestamp column doesn't crowd the edge.
        py: 0.5,
        pl: 1.5,
        pr: 1.5,
        mb: 0.5,
        borderLeft: 3,
        borderLeftColor: display.color,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        onClick={() => setOpen((o) => !o)}
        sx={{ cursor: 'pointer', minHeight: 28 }}
      >
        <Box
          aria-hidden
          sx={{
            fontSize: '1rem',
            lineHeight: 1,
            minWidth: 22,
            textAlign: 'center',
          }}
        >
          {display.emoji}
        </Box>
        <Chip
          size="small"
          label={display.label ?? evt.type}
          variant="outlined"
          // Custom height/font so the chip stays visually balanced
          // with the row's compact line-height. MUI's `size="small"`
          // chip is 24px tall — slightly too large after the row
          // padding shrunk.
          sx={{
            borderColor: display.color,
            color: display.color,
            fontWeight: 500,
            height: 20,
            fontSize: '0.7rem',
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
        <Chip
          size="small"
          label={evt.severity}
          color={severityChipColor(evt.severity)}
          variant="outlined"
          sx={{
            textTransform: 'capitalize',
            height: 20,
            fontSize: '0.7rem',
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
        <Chip
          size="small"
          label={evt.source}
          variant="outlined"
          sx={{
            textTransform: 'capitalize',
            height: 20,
            fontSize: '0.7rem',
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            fontSize: '0.8rem',
            lineHeight: 1.3,
            // Truncate gracefully — the operator clicks to expand
            // for the full payload anyway.
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {colorizePlatforms(summarizeEvent(evt))}
        </Typography>
        {/* Per-platform market deep-links — mirrors the Slack message
            builder's `marketLinks()` so both surfaces point at the same
            page. Click stops propagation so the row's expand/collapse
            toggle doesn't fire when the operator just wants to open
            the exchange. */}
        {notificationMarketLinks(evt).map((link) => (
          <IconButton
            key={link.platform}
            size="small"
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={`Open on ${link.platform === 'kalshi' ? 'Kalshi' : 'Polymarket'}`}
            sx={{
              color: PLATFORM_COLORS[link.platform],
              p: 0.25,
              '& svg': { fontSize: 14 },
            }}
          >
            <OpenInNewIcon />
          </IconButton>
        ))}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}
        >
          {formatTime(evt.occurredAt)}
        </Typography>
      </Stack>
      {open && (
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            bgcolor: 'background.default',
            borderRadius: 1,
          }}
        >
          {evt.ruleId && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<BugReportIcon sx={{ fontSize: 14 }} />}
              onClick={(e) => {
                e.stopPropagation();
                onOpenDebug(evt.ruleId!, evt.pairName ?? '');
              }}
              sx={{ mb: 1, fontSize: '0.7rem', py: 0.25 }}
            >
              Open dev logs
            </Button>
          )}
          <Stack spacing={0.5}>
            {eventDetailRows(evt).map(([label, value]) => (
              <Stack
                key={label}
                direction="row"
                spacing={2}
                alignItems="flex-start"
              >
                <Typography
                  variant="caption"
                  sx={{
                    minWidth: 200,
                    color: 'text.secondary',
                    flexShrink: 0,
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'common.white',
                    wordBreak: 'break-word',
                    flex: 1,
                  }}
                >
                  {value}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Paper>
  );
}

export function NotificationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<NotificationsFilter>(() =>
    readFilterFromParams(searchParams),
  );
  const [livePrepended, setLivePrepended] = useState<NotificationEvent[]>([]);

  const { data: typesData } = useNotificationTypes();
  const allTypes = typesData?.types ?? [];

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
  } = useNotifications(filter);

  // Persist filter in URL.
  useEffect(() => {
    const next = writeFilterToParams(filter);
    setSearchParams(next, { replace: true });
    // Reset live overlay when filters change — old events may not
    // match the new query.
    setLivePrepended([]);
  }, [filter, setSearchParams]);

  // Live append from WS.
  const { latestEvent, acknowledge, isConnected, reconnectCount } =
    useNotificationsSocket();
  // Audio cues for execution events (full + partial fills + cycle
  // complete). Wired up before the consume-and-acknowledge effect
  // below so React's effect ordering plays the cue on the same
  // render that the event lands in `latestEvent` — not after it
  // has already been acknowledged to null.
  useNotificationSound(latestEvent);
  useEffect(() => {
    if (!latestEvent) return;
    if (matchesFilter(latestEvent, filter)) {
      setLivePrepended((rows) => [latestEvent, ...rows]);
    }
    acknowledge();
  }, [latestEvent, filter, acknowledge]);

  // Refetch REST history whenever the WS reconnects. Redis pub/sub
  // is fire-and-forget — events fired during a gateway-pod restart
  // or a browser-tab-sleep window are lost on the realtime channel.
  // Mongo is the durable record, and refetching the latest page
  // surfaces any event the page would otherwise have missed. Also
  // clears the live overlay so the next render comes purely from
  // the freshly-fetched REST page (avoids dedupe edge cases when an
  // event arrived via both channels).
  useEffect(() => {
    if (reconnectCount === 0) return;
    setLivePrepended([]);
    refetch();
  }, [reconnectCount, refetch]);

  // Combined feed: live events on top, then react-query pages, deduped by id.
  const rows = useMemo<NotificationEvent[]>(() => {
    const seen = new Set<string>();
    const out: NotificationEvent[] = [];
    for (const e of livePrepended) {
      if (!seen.has(e.id)) {
        seen.add(e.id);
        out.push(e);
      }
    }
    if (data) {
      for (const page of data.pages) {
        for (const e of page.items) {
          if (!seen.has(e.id)) {
            seen.add(e.id);
            out.push(e);
          }
        }
      }
    }
    return out;
  }, [livePrepended, data]);

  // Drawer state for the per-rule dev-logs view, opened from the
  // "Open dev logs" button inside an expanded notification.
  const [debugRule, setDebugRule] = useState<{ ruleId: string; pairName: string } | null>(null);
  const handleOpenDebug = useCallback(
    (ruleId: string, pairName: string) => setDebugRule({ ruleId, pairName }),
    [],
  );
  const handleCloseDebug = useCallback(() => setDebugRule(null), []);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ color: 'common.white' }}>
          Notifications
        </Typography>
        <Chip
          size="small"
          color={isConnected ? 'success' : 'default'}
          label={isConnected ? 'live' : 'disconnected'}
        />
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={() => refetch()} title="Refresh">
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        {/*
          Three explicit rows so the filter labels never collide
          with the time-range chip Box (the previous flex-wrap
          layout overlapped on narrow widths because the chip
          group is taller than a single TextField/Select).

          Row 1 — Time range chips (full-width row).
          Row 2 — Free-text + Type multi-select (3 equal columns).
          Row 3 — Platform / Source / Severity / Clear (4 cols).

          All 2nd/3rd-row items use `flex: 1, minWidth: 160` so
          they expand to fill, but break to a column on xs.
        */}
        <Stack spacing={2}>
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 0.75 }}
            >
              Time range
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {PRESET_RANGES.map((r) => {
                const active =
                  r.minutes == null
                    ? !filter.from
                    : filter.from ===
                      new Date(
                        Math.floor(Date.now() / 60_000) * 60_000 -
                          r.minutes * 60_000,
                      ).toISOString();
                // Active match is approximate (rounded to minute) so
                // the chip stays highlighted even after a render pass.
                return (
                  <Chip
                    key={r.label}
                    label={r.label}
                    size="small"
                    variant={active ? 'filled' : 'outlined'}
                    color={active ? 'primary' : 'default'}
                    onClick={() =>
                      setFilter((f) => ({
                        ...f,
                        from:
                          r.minutes == null
                            ? undefined
                            : new Date(
                                Date.now() - r.minutes * 60_000,
                              ).toISOString(),
                        to: undefined,
                      }))
                    }
                  />
                );
              })}
            </Stack>
          </Box>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ md: 'center' }}
          >
            <TextField
              label="Pair Id"
              size="small"
              value={filter.pairId ?? ''}
              onChange={(e) =>
                setFilter((f) => ({
                  ...f,
                  pairId: e.target.value || undefined,
                }))
              }
              sx={{ flex: 1, minWidth: 160 }}
            />
            <TextField
              label="Rule Id"
              size="small"
              value={filter.ruleId ?? ''}
              onChange={(e) =>
                setFilter((f) => ({
                  ...f,
                  ruleId: e.target.value || undefined,
                }))
              }
              sx={{ flex: 1, minWidth: 160 }}
            />
            <FormControl size="small" sx={{ flex: 1, minWidth: 220 }}>
              <InputLabel id="type-filter">Type</InputLabel>
              <Select
                multiple
                labelId="type-filter"
                label="Type"
                value={filter.types ?? []}
                onChange={(e) =>
                  setFilter((f) => ({
                    ...f,
                    types:
                      typeof e.target.value === 'string'
                        ? (e.target.value
                            .split(',')
                            .filter(Boolean) as NotificationEventType[])
                        : (e.target.value as NotificationEventType[]),
                  }))
                }
                renderValue={(sel) => {
                  const ids = sel as string[];
                  if (ids.length === 0) return 'Any';
                  return ids
                    .map((t) => getNotificationDisplay(t).emoji)
                    .join(' ');
                }}
              >
                {allTypes.map((t) => {
                  const d = getNotificationDisplay(t);
                  return (
                    <MenuItem key={t} value={t}>
                      <Box
                        sx={{ minWidth: 28, textAlign: 'center', mr: 1 }}
                        aria-hidden
                      >
                        {d.emoji}
                      </Box>
                      <Box sx={{ color: d.color, fontWeight: 500 }}>
                        {d.label ?? t}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Stack>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ md: 'center' }}
          >
            <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
              <InputLabel id="platform-filter">Platform</InputLabel>
              <Select
                labelId="platform-filter"
                label="Platform"
                value={filter.platform ?? 'all'}
                onChange={(e) =>
                  setFilter((f) => ({
                    ...f,
                    platform: e.target.value as any,
                  }))
                }
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="kalshi">Kalshi</MenuItem>
                <MenuItem value="polymarket">Polymarket</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
              <InputLabel id="source-filter">Source</InputLabel>
              <Select
                labelId="source-filter"
                label="Source"
                value={filter.source ?? 'all'}
                onChange={(e) =>
                  setFilter((f) => ({ ...f, source: e.target.value as any }))
                }
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="automation">🤖 Automation</MenuItem>
                <MenuItem value="manual">✋ Manual</MenuItem>
                <MenuItem value="system">⚙️ System</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
              <InputLabel id="severity-filter">Severity</InputLabel>
              <Select
                labelId="severity-filter"
                label="Severity"
                value={filter.severity ?? 'all'}
                onChange={(e) =>
                  setFilter((f) => ({
                    ...f,
                    severity: e.target.value as any,
                  }))
                }
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </Select>
            </FormControl>
            <Button
              size="small"
              onClick={() =>
                setFilter({
                  platform: 'all',
                  source: 'all',
                  severity: 'all',
                })
              }
              sx={{ alignSelf: { md: 'center' }, minWidth: 80 }}
            >
              Clear
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load notifications.
        </Alert>
      )}

      {isFetching && rows.length === 0 ? (
        <Stack direction="row" alignItems="center" spacing={1}>
          <CircularProgress size={18} />
          <Typography variant="body2">Loading…</Typography>
        </Stack>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No notifications match the current filters.
        </Typography>
      ) : (
        <>
          {rows.map((evt) => (
            <NotificationRow key={evt.id} evt={evt} onOpenDebug={handleOpenDebug} />
          ))}
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" justifyContent="center">
            {hasNextPage ? (
              <Button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? 'Loading…' : 'Load older'}
              </Button>
            ) : (
              <Typography variant="caption" color="text.secondary">
                End of feed.
              </Typography>
            )}
          </Stack>
        </>
      )}

      <RuleLogsDrawer
        open={debugRule !== null}
        onClose={handleCloseDebug}
        ruleId={debugRule?.ruleId ?? null}
        pairName={debugRule?.pairName}
      />
    </Box>
  );
}

function matchesFilter(
  evt: NotificationEvent,
  filter: NotificationsFilter,
): boolean {
  if (filter.types && filter.types.length > 0 && !filter.types.includes(evt.type))
    return false;
  if (
    filter.platform &&
    filter.platform !== 'all' &&
    evt.platform !== filter.platform
  )
    return false;
  if (filter.source && filter.source !== 'all' && evt.source !== filter.source)
    return false;
  if (
    filter.severity &&
    filter.severity !== 'all' &&
    evt.severity !== filter.severity
  )
    return false;
  if (filter.ruleId && evt.ruleId !== filter.ruleId) return false;
  if (filter.pairId && evt.pairId !== filter.pairId) return false;
  if (filter.from) {
    const t = new Date(filter.from).getTime();
    if (new Date(evt.occurredAt).getTime() < t) return false;
  }
  return true;
}
